/**
 * Framer Motion 预设配置
 *
 * 常用 variants + transitions，避免组件里散写动效参数。
 */

import type { Transition, Variants } from 'framer-motion';

// ─────────────────────────────────────────────────────────────────────────────
// Spring 配置
// ─────────────────────────────────────────────────────────────────────────────

export const springs = {
  /** 默认弹簧：snappy 但平滑，用于大多数交互 */
  snappy: { type: 'spring', stiffness: 300, damping: 30 } as Transition,
  /** 柔和弹簧：用于 page transition、layout shift */
  gentle: { type: 'spring', stiffness: 120, damping: 14 } as Transition,
  /** 弹性弹簧：用于强调反馈 */
  bouncy: { type: 'spring', stiffness: 400, damping: 10 } as Transition,
  /** 重型弹簧：drawer/sheet 滑入 */
  drawer: { type: 'spring', stiffness: 280, damping: 25 } as Transition,
};

// ─────────────────────────────────────────────────────────────────────────────
// 缓动 transition（duration-based，需要确定时长时使用）
// ─────────────────────────────────────────────────────────────────────────────

export const tweens = {
  fast: { duration: 0.1, ease: [0.4, 0, 0.2, 1] } as Transition,
  base: { duration: 0.2, ease: [0.4, 0, 0.2, 1] } as Transition,
  slow: { duration: 0.3, ease: [0.4, 0, 0.2, 1] } as Transition,
  decelerate: { duration: 0.3, ease: [0, 0, 0.2, 1] } as Transition,
  accelerate: { duration: 0.2, ease: [0.4, 0, 1, 1] } as Transition,
};

// ─────────────────────────────────────────────────────────────────────────────
// Variants：常用动效模式
// ─────────────────────────────────────────────────────────────────────────────

/** 页面切换：淡入 + 微上浮 */
export const pageFadeVariants: Variants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: tweens.slow },
  exit: { opacity: 0, y: -8, transition: tweens.fast },
};

/** Drawer 右侧滑入 */
export const drawerVariants: Variants = {
  hidden: { x: '100%', opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: springs.drawer,
  },
  exit: {
    x: '100%',
    opacity: 0,
    transition: tweens.base,
  },
};

/** Modal/Dialog 中心淡入 + 缩放 */
export const modalVariants: Variants = {
  hidden: { opacity: 0, scale: 0.96, y: 8 },
  visible: { opacity: 1, scale: 1, y: 0, transition: springs.snappy },
  exit: { opacity: 0, scale: 0.98, y: 4, transition: tweens.fast },
};

/** 列表项错峰浮入 */
export const listContainerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.04, delayChildren: 0.04 },
  },
};

export const listItemVariants: Variants = {
  hidden: { opacity: 0, y: 6 },
  visible: { opacity: 1, y: 0, transition: tweens.base },
};

/** 命令面板（cmdk） */
export const commandPaletteVariants: Variants = {
  hidden: { opacity: 0, scale: 0.96, y: -8 },
  visible: { opacity: 1, scale: 1, y: 0, transition: springs.snappy },
  exit: { opacity: 0, scale: 0.98, y: -4, transition: tweens.fast },
};

// ─────────────────────────────────────────────────────────────────────────────
// 微交互 props（直接 spread 到 motion 组件）
// ─────────────────────────────────────────────────────────────────────────────

/** Hover lift：上浮 + 微缩放 — 用于卡片 */
export const hoverLiftProps = {
  whileHover: { y: -2, scale: 1.005, transition: tweens.fast },
  whileTap: { scale: 0.99, transition: tweens.fast },
};

/** Tap scale：纯按压反馈 — 用于按钮 */
export const tapScaleProps = {
  whileTap: { scale: 0.97, transition: tweens.fast },
};

/** 选中态发光：用 layoutId 跨组件 */
export const SELECTED_LAYOUT_ID = 'folo-selected-indicator';
